import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { useProject } from './ProjectContext'
import { useI18n } from './I18nContext'
import { normalizeBuildPlan, layoutPlan } from '../utils/planBuilder'

const AIContext = createContext()

// Maximum length of a single user chat message. Keep in sync with
// MAX_MESSAGE_CHARS in convex/aiActions.ts. Validated client-side too so the
// user gets a friendly, immediate warning (and keeps their typed text)
// instead of a raw server error after a wasted round-trip.
export const MAX_MESSAGE_LENGTH = 20000

// Turn a raw error (often a Convex-wrapped server error string like
// "[CONVEX A(aiActions:chat)] [Request ID: …] Server Error Uncaught Error:
// Message too long … at handler (…)") into a short, friendly, localized
// message suitable for showing in the chat.
function humanizeChatError(raw, isNb) {
  const fallback = isNb
    ? 'Klarte ikke å hente svar fra Teddo. Prøv igjen.'
    : 'Failed to get a response from Teddo. Please try again.'
  if (!raw) return fallback
  let msg = String(raw)
  // Pull out the "Uncaught Error: <message>" payload Convex wraps around
  // server-thrown errors, dropping the request id and stack frame noise.
  const m = msg.match(/Uncaught\s+\w*Error:\s*([^\n]*?)(?:\s+at\s+|\n|$)/i)
  if (m && m[1]) msg = m[1].trim()
  msg = msg.replace(/\s*Called by client\s*$/i, '').trim()

  if (/message too long/i.test(msg)) {
    return isNb
      ? 'Meldingen er for lang. Del den opp i kortere meldinger, eller legg lange plandetaljer i prosjektbeskrivelsen.'
      : 'That message is too long. Split it into shorter messages, or add long plan details to the project description.'
  }
  if (/no api key/i.test(msg)) {
    return isNb
      ? 'Ingen API-nøkkel er satt opp. Legg den inn i AI-innstillinger.'
      : 'No API key is configured. Add one in AI Settings.'
  }
  if (/authentication required/i.test(msg)) {
    return isNb
      ? 'Du må være innlogget for å bruke Teddo.'
      : 'You need to be signed in to use Teddo.'
  }
  return msg || fallback
}

// Compute a short, human-readable summary of what changed between two
// project-context snapshots. Used so Teddo automatically "sees" edits
// the user made between turns without needing a refresh button.
function diffSnapshots(prev, next) {
  if (!prev || !next) return ''
  const lines = []
  try {
    if (prev.projectName !== next.projectName) {
      lines.push(`Project renamed: "${prev.projectName}" -> "${next.projectName}"`)
    }
    if ((prev.description || '') !== (next.description || '')) {
      lines.push('Project description updated.')
    }
    const prevFlows = new Map((prev.flows || []).map(f => [f.id, f]))
    const nextFlows = new Map((next.flows || []).map(f => [f.id, f]))
    for (const [id, f] of nextFlows) {
      if (!prevFlows.has(id)) lines.push(`Added flow "${f.name}".`)
    }
    for (const [id, f] of prevFlows) {
      if (!nextFlows.has(id)) lines.push(`Removed flow "${f.name}".`)
    }
    for (const [id, nf] of nextFlows) {
      const pf = prevFlows.get(id)
      if (!pf) continue
      if (pf.name !== nf.name) lines.push(`Renamed flow "${pf.name}" -> "${nf.name}".`)
      const prevCps = new Map((pf.checkpoints || []).map(c => [c.id, c]))
      const nextCps = new Map((nf.checkpoints || []).map(c => [c.id, c]))
      for (const [cid, nc] of nextCps) {
        if (!prevCps.has(cid)) lines.push(`Added checkpoint "${nc.name}" in flow "${nf.name}".`)
      }
      for (const [cid, pc] of prevCps) {
        if (!nextCps.has(cid)) lines.push(`Removed checkpoint "${pc.name}" from flow "${nf.name}".`)
      }
      for (const [cid, nc] of nextCps) {
        const pc = prevCps.get(cid)
        if (!pc) continue
        if (pc.name !== nc.name) lines.push(`Renamed checkpoint "${pc.name}" -> "${nc.name}" in flow "${nf.name}".`)
        if (pc.status !== nc.status) lines.push(`Checkpoint "${nc.name}" status: ${pc.status} -> ${nc.status}.`)
        if ((pc.estimatedDuration || 0) !== (nc.estimatedDuration || 0)) {
          lines.push(`Checkpoint "${nc.name}" duration: ${pc.estimatedDuration || 0}min -> ${nc.estimatedDuration || 0}min.`)
        }
      }
    }
    const prevDeps = (prev.dependencies || []).length
    const nextDeps = (next.dependencies || []).length
    if (prevDeps !== nextDeps) {
      lines.push(`Dependencies: ${prevDeps} -> ${nextDeps}.`)
    }
  } catch {
    return ''
  }
  return lines.slice(0, 30).join('\n')
}

export function useAI() {
  const context = useContext(AIContext)
  if (!context) {
    throw new Error('useAI must be used within AIProvider')
  }
  return context
}

export function AIProvider({ children }) {
  const {
    project, currentProjectId,
    addFlow, updateFlow, deleteFlow, toggleFlowVisibility,
    addCheckpoint, updateCheckpoint, deleteCheckpoint,
    addSubtask, toggleSubtask, deleteSubtask,
    addDependency, removeDependency,
    updateProject,
  } = useProject()
  const { language: uiLanguage } = useI18n()
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState(null)
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const abortControllerRef = useRef(null)
  const autoSaveTimerRef = useRef(null)

  // Tracks the project snapshot the AI most recently "saw" so we can
  // compute a small diff on the next turn and append it to the prompt.
  // Stored in a ref (not state) since it's only read inside sendMessage.
  const lastSnapshotRef = useRef(null)

  // AI settings via Convex reactive query
  const settings = useQuery(api.ai.getSettings) || null
  const updateSettingsMutation = useMutation(api.ai.updateSettings)

  // Available AI models for the picker
  const availableModels = useQuery(api.ai.listModels) || []
  const currentModel = settings?.aiModel ||
    (settings?.aiProvider === 'anthropic' ? 'claude-sonnet-5' : 'gpt-4o')
  const setModel = useCallback(async (modelId) => {
    await updateSettingsMutation({ aiModel: modelId })
  }, [updateSettingsMutation])

  // Teddo memory (per project + user, persistent)
  const teddoMemory = useQuery(
    api.aiMemory.get,
    currentProjectId ? { projectId: currentProjectId } : 'skip'
  ) || null
  const addDismissedMutation = useMutation(api.aiMemory.addDismissed)
  const addAcceptedMutation = useMutation(api.aiMemory.addAccepted)
  const addNoteMutation = useMutation(api.aiMemory.addNote)
  const removeNoteMutation = useMutation(api.aiMemory.removeNote)
  const allowDismissedMutation = useMutation(api.aiMemory.allowDismissed)
  const clearMemoryMutation = useMutation(api.aiMemory.clear)

  // AI sessions
  const sessions = useQuery(
    api.aiSessions.list,
    currentProjectId ? { projectId: currentProjectId } : 'skip'
  ) || []
  const archivedSessions = useQuery(
    api.aiSessions.listArchived,
    currentProjectId ? { projectId: currentProjectId } : 'skip'
  ) || []
  const createSessionMutation = useMutation(api.aiSessions.create)
  const saveSessionMutation = useMutation(api.aiSessions.save)
  const archiveSessionMutation = useMutation(api.aiSessions.archive)
  const unarchiveSessionMutation = useMutation(api.aiSessions.unarchive)
  const removeSessionMutation = useMutation(api.aiSessions.remove)

  // Non-streaming chat action (fallback)
  const chatAction = useAction(api.aiActions.chat)
  const summarizeAction = useAction(api.aiActions.summarize)

  // Auto-select the most recent session when project changes and sessions load
  useEffect(() => {
    if (!currentProjectId) {
      setCurrentSessionId(null)
      setMessages([])
      return
    }
    if (sessions.length > 0 && !currentSessionId) {
      const latest = sessions[0] // list is ordered by updatedAt desc
      setCurrentSessionId(latest._id)
      try {
        setMessages(JSON.parse(latest.messages) || [])
      } catch {
        setMessages([])
      }
    }
  }, [currentProjectId, sessions.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load messages when switching sessions
  const switchSession = useCallback((sessionId) => {
    const session = sessions.find(s => s._id === sessionId)
    if (!session) return
    setCurrentSessionId(sessionId)
    try {
      setMessages(JSON.parse(session.messages) || [])
    } catch {
      setMessages([])
    }
    setError(null)
  }, [sessions])

  // Auto-save messages to current session (debounced 2s)
  useEffect(() => {
    if (!currentSessionId || messages.length === 0) return
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        await saveSessionMutation({ id: currentSessionId, messages: JSON.stringify(messages) })
        // Update session title from first user message if title is empty
        const firstUserMsg = messages.find(m => m.role === 'user')
        if (firstUserMsg) {
          const title = firstUserMsg.content.slice(0, 40) + (firstUserMsg.content.length > 40 ? '…' : '')
          const session = sessions.find(s => s._id === currentSessionId)
          if (session && session.title === 'New Session') {
            await saveSessionMutation({ id: currentSessionId, messages: JSON.stringify(messages), title })
          }
        }
      } catch (err) {
        console.error('Failed to auto-save session:', err)
      }
    }, 2000)
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    }
  }, [messages, currentSessionId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Create a new session
  const createSession = useCallback(async () => {
    if (!currentProjectId) return
    const sessionId = await createSessionMutation({
      projectId: currentProjectId,
      title: 'New Session',
    })
    setCurrentSessionId(sessionId)
    setMessages([])
    setError(null)
    return sessionId
  }, [currentProjectId, createSessionMutation])

  // Archive a session
  const archiveSession = useCallback(async (sessionId) => {
    await archiveSessionMutation({ id: sessionId })
    if (sessionId === currentSessionId) {
      setCurrentSessionId(null)
      setMessages([])
    }
  }, [currentSessionId, archiveSessionMutation])

  // Summarize a session's chat into a one-line takeaway and pin it to memory.
  // Returns the pinned text, or null if nothing durable was found.
  const summarizeAndPinSession = useCallback(async (sessionId) => {
    if (!currentProjectId) return null
    // Prefer in-memory transcript when summarizing the current session,
    // otherwise fall back to the stored session messages.
    let transcript = []
    if (sessionId === currentSessionId) {
      transcript = messages
    } else {
      const s = sessions.find(x => x._id === sessionId)
        || archivedSessions.find(x => x._id === sessionId)
      if (s) {
        try { transcript = JSON.parse(s.messages) || [] } catch { transcript = [] }
      }
    }
    if (!transcript.length) return null
    const compact = transcript
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role, content: String(m.content || '').slice(0, 4000) }))
    try {
      const lang = project?.language || uiLanguage
      const res = await summarizeAction({ messages: compact, language: lang })
      const text = (res?.summary || '').trim()
      if (!text) return null
      await addNoteMutation({ projectId: currentProjectId, text })
      return text
    } catch (err) {
      console.warn('summarizeAndPinSession failed:', err)
      return null
    }
  }, [currentProjectId, currentSessionId, messages, sessions, archivedSessions, project, uiLanguage, summarizeAction, addNoteMutation])

  // Unarchive a session and switch to it
  const unarchiveSession = useCallback(async (sessionId) => {
    await unarchiveSessionMutation({ id: sessionId })
    const restored = archivedSessions.find(s => s._id === sessionId)
    setCurrentSessionId(sessionId)
    if (restored) {
      try {
        setMessages(JSON.parse(restored.messages) || [])
      } catch {
        setMessages([])
      }
    } else {
      setMessages([])
    }
    setError(null)
  }, [unarchiveSessionMutation, archivedSessions])

  // Permanently delete a session
  const removeSession = useCallback(async (sessionId) => {
    await removeSessionMutation({ id: sessionId })
    if (sessionId === currentSessionId) {
      setCurrentSessionId(null)
      setMessages([])
    }
  }, [currentSessionId, removeSessionMutation])

  // Build project context for AI
  const buildProjectContext = useCallback(() => {
    if (!project || !currentProjectId) {
      return null
    }

    // Calculate statistics
    const totalFlows = project.flows?.length || 0
    const totalCheckpoints = project.flows?.reduce((sum, flow) => sum + (flow.checkpoints?.length || 0), 0) || 0
    const completedCheckpoints = project.flows?.reduce((sum, flow) => {
      return sum + (flow.checkpoints?.filter(cp => cp.status === 'completed')?.length || 0)
    }, 0) || 0

    // Calculate total time
    const totalMinutes = project.flows?.reduce((sum, flow) => {
      return sum + (flow.checkpoints?.reduce((s, cp) => s + (cp.estimatedDuration || 0), 0) || 0)
    }, 0) || 0

    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    const totalEstimatedTime = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`

    // Build checkpoint ID -> name lookup for resolving dependencies
    const checkpointNameMap = {}
    project.flows?.forEach(flow => {
      flow.checkpoints?.forEach(cp => {
        checkpointNameMap[cp.id] = { name: cp.name, flowName: flow.name }
      })
    })

    // Build detailed flows with checkpoints and subtasks
    // Include id so the AI can reference flows/checkpoints in tool calls
    const flows = project.flows?.map(flow => ({
      id: flow.id,
      name: flow.name,
      color: flow.color,
      hidden: flow.hidden || false,
      checkpoints: flow.checkpoints?.map(cp => ({
        id: cp.id,
        name: cp.name,
        time: cp.time,
        distance: cp.distance,
        status: cp.status,
        type: cp.type,
        estimatedDuration: cp.estimatedDuration || 0,
        subtasks: cp.subtasks?.map(st => ({
          text: st.text,
          done: st.done
        })) || []
      })) || []
    })) || []

    // Resolve dependencies to human-readable names
    const dependencies = project.dependencies?.map(dep => ({
      fromCheckpoint: checkpointNameMap[dep.from]?.name || dep.from,
      fromFlow: checkpointNameMap[dep.from]?.flowName || 'Unknown',
      toCheckpoint: checkpointNameMap[dep.to]?.name || dep.to,
      toFlow: checkpointNameMap[dep.to]?.flowName || 'Unknown',
      type: dep.type
    })) || []

    const fullContext = {
      projectName: project.name,
      description: project.description,
      startDate: project.startDate,
      timeUnit: project.timeUnit,
      distanceLabels: project.distanceLabels,
      language: project.language || uiLanguage,
      statistics: {
        totalFlows,
        totalCheckpoints,
        completedCheckpoints,
        totalEstimatedTime
      },
      flows,
      dependencies
    }

    // If context is too large, fall back to a compact-but-complete
    // shape that still names every checkpoint (so Teddo can reason about
    // it). We drop subtasks first; dependencies are kept.
    const serialized = JSON.stringify(fullContext)
    if (serialized.length > 24000) {
      return {
        projectName: project.name,
        description: project.description,
        startDate: project.startDate,
        timeUnit: project.timeUnit,
        language: project.language || uiLanguage,
        statistics: {
          totalFlows,
          totalCheckpoints,
          completedCheckpoints,
          totalEstimatedTime
        },
        flows: project.flows?.map(flow => ({
          id: flow.id,
          name: flow.name,
          hidden: flow.hidden || false,
          checkpoints: flow.checkpoints?.map(cp => ({
            id: cp.id,
            name: cp.name,
            status: cp.status,
            time: cp.time,
            distance: cp.distance,
            estimatedDuration: cp.estimatedDuration || 0
            // subtasks intentionally omitted in compact mode
          })) || []
        })) || [],
        dependencies,
        _note: 'Compact context — subtasks omitted due to project size'
      }
    }

    return fullContext
  }, [project, currentProjectId, uiLanguage])

  // Settings are reactive via useQuery, no need to fetch
  const fetchSettings = useCallback(async () => {
    return settings
  }, [settings])

  // Send message to AI via HTTP streaming endpoint
  const sendMessage = useCallback(async (userMessage) => {
    if (!userMessage?.trim()) return

    // Guard over-long messages up front so the user gets an instant, friendly
    // warning instead of a raw server error after a wasted round-trip. The
    // caller is responsible for preserving the user's text on rejection.
    if (userMessage.length > MAX_MESSAGE_LENGTH) {
      const isNb = (project?.language || uiLanguage) === 'nb'
      setError(isNb
        ? `Meldingen er for lang (${userMessage.length.toLocaleString('nb-NO')} tegn, maks ${MAX_MESSAGE_LENGTH.toLocaleString('nb-NO')}). Del den opp i kortere meldinger, eller legg lange plandetaljer i prosjektbeskrivelsen.`
        : `Message is too long (${userMessage.length.toLocaleString()} characters, max ${MAX_MESSAGE_LENGTH.toLocaleString()}). Split it into shorter messages, or add long plan details to the project description.`)
      return false
    }

    // Auto-create a session if none exists so messages get persisted
    let activeSessionId = currentSessionId
    if (!activeSessionId && currentProjectId) {
      try {
        activeSessionId = await createSessionMutation({
          projectId: currentProjectId,
          title: 'New Session',
        })
        setCurrentSessionId(activeSessionId)
      } catch (err) {
        console.error('Failed to create session:', err)
      }
    }

    // Add user message to chat
    const userMsg = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }

    setMessages(prev => [...prev, userMsg])
    setError(null)
    setIsStreaming(true)

    // Prepare conversation history (exclude current message)
    const conversationHistory = messages.map(msg => ({
      role: msg.role,
      content: msg.content
    }))

    // Build project context
    const projectContext = buildProjectContext()

    // Compute a small diff of what changed since Teddo's last turn so the
    // model can refresh its mental model without the user asking. We
    // snapshot at the end of this turn.
    let recentChanges = ''
    if (projectContext && lastSnapshotRef.current) {
      recentChanges = diffSnapshots(lastSnapshotRef.current, projectContext)
    }
    if (projectContext) {
      if (recentChanges) projectContext.recentChanges = recentChanges
      if (teddoMemory) {
        projectContext.teddoMemory = {
          pinnedNotes: teddoMemory.pinnedNotes || [],
          acceptedSuggestions: teddoMemory.acceptedSuggestions || [],
          dismissedSuggestions: teddoMemory.dismissedSuggestions || [],
        }
      }
    }

    try {
      // Add a placeholder assistant message while waiting
      const placeholder = { role: 'assistant', content: '…', timestamp: new Date().toISOString() }
      setMessages(prev => [...prev, placeholder])

      const responseContent = await chatAction({
        message: userMessage,
        conversationHistory,
        projectContext: projectContext ? JSON.stringify(projectContext) : undefined,
        model: currentModel,
      })

      // responseContent is { content, suggestions, questions, model }
      const responseText = typeof responseContent === 'string' ? responseContent : responseContent.content
      const responseSuggestions = (typeof responseContent === 'object' && responseContent.suggestions) || []
      const responseQuestions = (typeof responseContent === 'object' && responseContent.questions) || []
      const responseModel = (typeof responseContent === 'object' && responseContent.model) || undefined

      // Replace placeholder with real response
      setMessages(prev => {
        const updated = [...prev]
        updated[updated.length - 1] = {
          role: 'assistant',
          content: responseText,
          suggestions: responseSuggestions,
          questions: responseQuestions,
          model: responseModel,
          timestamp: new Date().toISOString()
        }
        return updated
      })

      setIsStreaming(false)

      // Update snapshot so the next diff is relative to this turn.
      if (projectContext) {
        lastSnapshotRef.current = projectContext
      }
      return true
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('AI request cancelled')
      } else {
        console.error('AI chat error:', error)
        const isNb = (project?.language || uiLanguage) === 'nb'
        setError(humanizeChatError(error.message, isNb))
        // Remove the placeholder assistant message
        setMessages(prev => prev.slice(0, -1))
      }
      setIsStreaming(false)
      return false
    }
  }, [messages, buildProjectContext, chatAction, currentSessionId, currentProjectId, createSessionMutation, teddoMemory, currentModel, project, uiLanguage])

  // Resolve a flowId from the suggestion — the AI may send either the real id
  // or just the flow name. Fall back to name matching so suggestions work either way.
  const resolveFlowId = useCallback((flowId) => {
    if (!project?.flows) return null
    const byId = project.flows.find(f => f.id === flowId)
    if (byId) return byId.id
    // Try matching by name (case-insensitive, partial match)
    const byName = project.flows.find(f =>
      f.name.toLowerCase() === (flowId || '').toLowerCase() ||
      f.name.toLowerCase().includes((flowId || '').toLowerCase()) ||
      (flowId || '').toLowerCase().includes(f.name.toLowerCase())
    )
    return byName?.id || null
  }, [project])

  // Resolve a checkpointId within a flow — same id-first, then name fallback.
  // If `flowId` is null/unresolvable, scan across all flows for a name match
  // so suggestions that omit (or mis-name) the flow still apply.
  const resolveCheckpointId = useCallback((flowId, checkpointId) => {
    if (!project?.flows) return null
    const target = (checkpointId || '').toLowerCase().trim()
    if (!target) return null
    const flow = flowId ? project.flows.find(f => f.id === flowId) : null
    const tryFlow = (f) => {
      if (!f?.checkpoints) return null
      const byId = f.checkpoints.find(cp => cp.id === checkpointId)
      if (byId) return byId.id
      const byName = f.checkpoints.find(cp =>
        cp.name.toLowerCase() === target ||
        cp.name.toLowerCase().includes(target) ||
        target.includes(cp.name.toLowerCase())
      )
      return byName?.id || null
    }
    if (flow) {
      const hit = tryFlow(flow)
      if (hit) return hit
    }
    // Fall back: search every flow for the checkpoint.
    for (const f of project.flows) {
      if (f === flow) continue
      const hit = tryFlow(f)
      if (hit) return hit
    }
    return null
  }, [project])

  // Given a checkpointId, return the flow it belongs to. Used when an
  // AI suggestion's flowId is wrong/missing but the checkpoint name uniquely
  // identifies the location.
  const flowOfCheckpoint = useCallback((checkpointId) => {
    if (!project?.flows || !checkpointId) return null
    for (const f of project.flows) {
      if (f.checkpoints?.some(cp => cp.id === checkpointId)) return f.id
    }
    return null
  }, [project])

  // Apply a suggestion from the AI to the project.
  // `overrides` lets the user Modify before accepting.
  const applySuggestion = useCallback((suggestion, overrides = {}) => {
    const s = { ...suggestion, ...overrides }
    const fail = (reason) => {
      // Include a snapshot of the current id sets so we can tell whether
      // the failure is a hallucinated id vs. stale local project state
      // (e.g. isDirty gate suppressing reactive Convex updates).
      const flowIds = (project?.flows || []).map(f => f.id)
      const checkpointIds = (project?.flows || []).flatMap(f =>
        (f.checkpoints || []).map(c => c.id)
      )
      console.warn('applySuggestion failed:', reason, {
        suggestion: s,
        knownFlowIds: flowIds,
        knownCheckpointIds: checkpointIds,
      })
      return { ok: false, reason }
    }
    let result = { ok: true }
    switch (s.type) {
      case 'create_plan': {
        // Build a whole plan in one approval. Create flows first, then their
        // checkpoints (so ids resolve), then dependencies matched by name.
        // The AI may define the Y-axis stops (distanceLabels) and place each
        // checkpoint at a real distance; layoutPlan keeps those and only fills
        // in what's missing (time spacing, and lanes for unplaced flows).
        const newLabels = Array.isArray(s.distanceLabels)
          && s.distanceLabels.filter(l => typeof l === 'string' && l.trim()).length >= 2
          ? s.distanceLabels.map(l => String(l).trim()).filter(Boolean)
          : null
        if (newLabels) updateProject({ distanceLabels: newLabels })
        const labelsForRange = newLabels || project?.distanceLabels
        const timeStart = Number(project?.xAxisStart) || 0
        const timeRange = (Number(project?.xAxisEnd) || 12) - timeStart
        const distanceRange = ((labelsForRange?.length || 5) - 1) || 4
        const { flows, dependencies } = layoutPlan(
          normalizeBuildPlan(s),
          { timeStart, timeRange, distanceRange, layout: s.layout }
        )
        if (flows.length === 0) { result = fail('Plan has no flows'); break }
        const keyOf = (flowName, cpName) =>
          `${(flowName || '').toLowerCase()} ${(cpName || '').toLowerCase()}`
        const cpIdByKey = {}   // "flow\0checkpoint" -> id (precise)
        const cpIdByName = {}  // "checkpoint" -> id (fallback when flow name is off)
        for (const f of flows) {
          const createdFlow = addFlow(f.name, f.color)
          if (!createdFlow?.id) continue
          for (const c of f.checkpoints) {
            const createdCp = addCheckpoint(createdFlow.id, c.name, c.time, c.distance)
            if (!createdCp?.id) continue
            const patch = {}
            if (c.estimatedDuration !== undefined) patch.estimatedDuration = c.estimatedDuration
            if (c.status !== undefined) patch.status = c.status
            if (Object.keys(patch).length) updateCheckpoint(createdFlow.id, createdCp.id, patch)
            for (const stText of c.subtasks) addSubtask(createdFlow.id, createdCp.id, stText)
            cpIdByKey[keyOf(f.name, c.name)] = createdCp.id
            cpIdByName[(c.name || '').toLowerCase()] = createdCp.id
          }
        }
        for (const d of dependencies) {
          const fromId = cpIdByKey[keyOf(d.fromFlow, d.fromCheckpoint)]
            || cpIdByName[(d.fromCheckpoint || '').toLowerCase()]
          const toId = cpIdByKey[keyOf(d.toFlow, d.toCheckpoint)]
            || cpIdByName[(d.toCheckpoint || '').toLowerCase()]
          if (fromId && toId && fromId !== toId) addDependency(fromId, toId)
        }
        break
      }
      case 'update_project':
        updateProject({
          ...(s.name !== undefined ? { name: s.name } : {}),
          ...(s.description_text !== undefined ? { description: s.description_text } : {}),
          ...(s.startDate !== undefined ? { startDate: s.startDate } : {}),
          ...(s.timeUnit !== undefined ? { timeUnit: s.timeUnit } : {}),
        })
        break
      case 'update_description':
        updateProject({ description: s.content })
        break
      case 'add_flow':
        addFlow(s.name || 'New Flow', s.color)
        break
      case 'update_flow': {
        const flowId = resolveFlowId(s.flowId)
        if (!flowId) { result = fail(`Could not find flow "${s.flowId}"`); break }
        const patch = {}
        if (s.name !== undefined) patch.name = s.name
        if (s.color !== undefined) patch.color = s.color
        if (s.hidden !== undefined) patch.hidden = s.hidden
        updateFlow(flowId, patch)
        break
      }
      case 'delete_flow': {
        const flowId = resolveFlowId(s.flowId)
        if (!flowId) { result = fail(`Could not find flow "${s.flowId}"`); break }
        deleteFlow(flowId)
        break
      }
      case 'add_checkpoint': {
        const flowId = resolveFlowId(s.flowId)
        if (!flowId) { result = fail(`Could not find flow "${s.flowId}"`); break }
        const cp = addCheckpoint(flowId, s.name, s.time ?? 1, s.distance ?? 1)
        if (cp && (s.estimatedDuration !== undefined || s.status !== undefined)) {
          const patch = {}
          if (s.estimatedDuration !== undefined) patch.estimatedDuration = s.estimatedDuration
          if (s.status !== undefined) patch.status = s.status
          updateCheckpoint(flowId, cp.id, patch)
        }
        break
      }
      case 'update_checkpoint': {
        let flowId = resolveFlowId(s.flowId)
        const checkpointId = resolveCheckpointId(flowId, s.checkpointId)
        if (!checkpointId) { result = fail(`Could not find checkpoint "${s.checkpointId}"`); break }
        // Always recompute the flow from the checkpoint so a wrong-but-resolvable
        // s.flowId (e.g. fuzzy match into the wrong flow) doesn't cause a silent
        // no-op when the checkpoint actually lives in a different flow.
        const actualFlowId = flowOfCheckpoint(checkpointId)
        if (!actualFlowId) { result = fail(`Could not find flow for checkpoint "${s.checkpointId}"`); break }
        flowId = actualFlowId
        const patch = {}
        if (s.name !== undefined) patch.name = s.name
        if (s.time !== undefined) patch.time = s.time
        if (s.distance !== undefined) patch.distance = s.distance
        if (s.estimatedDuration !== undefined) patch.estimatedDuration = s.estimatedDuration
        if (s.status !== undefined) patch.status = s.status
        updateCheckpoint(flowId, checkpointId, patch)
        break
      }
      case 'update_checkpoint_status': {
        const flowHint = resolveFlowId(s.flowId)
        const checkpointId = resolveCheckpointId(flowHint, s.checkpointId)
        if (!checkpointId) { result = fail(`Could not find checkpoint "${s.checkpointId}"`); break }
        const flowId = flowOfCheckpoint(checkpointId)
        if (!flowId) { result = fail(`Could not find flow for checkpoint "${s.checkpointId}"`); break }
        updateCheckpoint(flowId, checkpointId, { status: s.status })
        break
      }
      case 'delete_checkpoint': {
        const flowHint = resolveFlowId(s.flowId)
        const checkpointId = resolveCheckpointId(flowHint, s.checkpointId)
        if (!checkpointId) { result = fail(`Could not find checkpoint "${s.checkpointId}"`); break }
        const flowId = flowOfCheckpoint(checkpointId)
        if (!flowId) { result = fail(`Could not find flow for checkpoint "${s.checkpointId}"`); break }
        deleteCheckpoint(flowId, checkpointId)
        break
      }
      case 'add_subtask': {
        const flowHint = resolveFlowId(s.flowId)
        const checkpointId = resolveCheckpointId(flowHint, s.checkpointId)
        if (!checkpointId) { result = fail(`Could not find checkpoint "${s.checkpointId}"`); break }
        const flowId = flowOfCheckpoint(checkpointId)
        if (!flowId) { result = fail(`Could not find flow for checkpoint "${s.checkpointId}"`); break }
        addSubtask(flowId, checkpointId, s.text)
        break
      }
      case 'toggle_subtask': {
        const flowHint = resolveFlowId(s.flowId)
        const checkpointId = resolveCheckpointId(flowHint, s.checkpointId)
        if (!checkpointId || typeof s.subtaskIndex !== 'number') {
          result = fail(`Could not resolve subtask`); break
        }
        const flowId = flowOfCheckpoint(checkpointId)
        if (!flowId) { result = fail(`Could not find flow for checkpoint "${s.checkpointId}"`); break }
        toggleSubtask(flowId, checkpointId, s.subtaskIndex)
        break
      }
      case 'delete_subtask': {
        const flowHint = resolveFlowId(s.flowId)
        const checkpointId = resolveCheckpointId(flowHint, s.checkpointId)
        if (!checkpointId || typeof s.subtaskIndex !== 'number') {
          result = fail(`Could not resolve subtask`); break
        }
        const flowId = flowOfCheckpoint(checkpointId)
        if (!flowId) { result = fail(`Could not find flow for checkpoint "${s.checkpointId}"`); break }
        deleteSubtask(flowId, checkpointId, s.subtaskIndex)
        break
      }
      case 'add_dependency': {
        let fromFlowId = resolveFlowId(s.fromFlowId)
        const fromCheckpointId = resolveCheckpointId(fromFlowId, s.fromCheckpointId)
        let toFlowId = resolveFlowId(s.toFlowId)
        const toCheckpointId = resolveCheckpointId(toFlowId, s.toCheckpointId)
        if (!fromCheckpointId || !toCheckpointId) { result = fail(`Could not resolve dependency endpoints`); break }
        addDependency(fromCheckpointId, toCheckpointId)
        break
      }
      case 'delete_dependency': {
        let fromFlowId = resolveFlowId(s.fromFlowId)
        const fromCheckpointId = resolveCheckpointId(fromFlowId, s.fromCheckpointId)
        let toFlowId = resolveFlowId(s.toFlowId)
        const toCheckpointId = resolveCheckpointId(toFlowId, s.toCheckpointId)
        if (!fromCheckpointId || !toCheckpointId) { result = fail(`Could not resolve dependency endpoints`); break }
        removeDependency(fromCheckpointId, toCheckpointId)
        break
      }
      case 'pin_note': {
        const text = (s.text || '').trim()
        if (!text) { result = fail(`Empty note text`); break }
        if (!currentProjectId) { result = fail(`No project selected`); break }
        // Fire-and-forget; backend rejects empty/too-long.
        addNoteMutation({ projectId: currentProjectId, text }).catch(err => {
          console.warn('pin_note failed:', err)
        })
        break
      }
      default:
        result = fail(`Unknown suggestion type: ${s.type}`)
    }

    if (!result.ok) return result

    // Persist in Teddo memory so future turns know it's already in the plan.
    if (currentProjectId) {
      addAcceptedMutation({
        projectId: currentProjectId,
        id: s.id || `${s.type}-${Date.now()}`,
        type: s.type,
        summary: s.description || '',
        modified: Boolean(s.modified),
      }).catch(err => console.warn('addAccepted failed:', err))
    }
    return result
  }, [
    project, currentProjectId, resolveFlowId, resolveCheckpointId, flowOfCheckpoint,
    updateProject, addFlow, updateFlow, deleteFlow,
    addCheckpoint, updateCheckpoint, deleteCheckpoint,
    addSubtask, toggleSubtask, deleteSubtask,
    addDependency, removeDependency,
    addAcceptedMutation, addNoteMutation,
  ])

  // Dismiss a suggestion so Teddo won't propose it again.
  const dismissSuggestion = useCallback((suggestion) => {
    if (!currentProjectId) return
    addDismissedMutation({
      projectId: currentProjectId,
      id: suggestion.id || `${suggestion.type}-${Date.now()}`,
      type: suggestion.type,
      summary: suggestion.description || '',
    }).catch(err => console.warn('addDismissed failed:', err))
  }, [currentProjectId, addDismissedMutation])

  // ─── Teddo memory wrappers (inject current projectId) ───
  const addPinnedNote = useCallback((text) => {
    if (!currentProjectId) return Promise.resolve(null)
    return addNoteMutation({ projectId: currentProjectId, text })
  }, [currentProjectId, addNoteMutation])

  const removePinnedNote = useCallback((noteId) => {
    if (!currentProjectId) return Promise.resolve(null)
    return removeNoteMutation({ projectId: currentProjectId, noteId })
  }, [currentProjectId, removeNoteMutation])

  const allowDismissedSuggestion = useCallback((id) => {
    if (!currentProjectId) return Promise.resolve(null)
    return allowDismissedMutation({ projectId: currentProjectId, id })
  }, [currentProjectId, allowDismissedMutation])

  const clearTeddoMemory = useCallback(() => {
    if (!currentProjectId) return Promise.resolve(null)
    return clearMemoryMutation({ projectId: currentProjectId })
  }, [currentProjectId, clearMemoryMutation])


  // Cancel ongoing request (best-effort — Convex actions can't be mid-flight cancelled)
  const cancelRequest = useCallback(() => {
    abortControllerRef.current = null
    setIsStreaming(false)
  }, [])

  // Clear chat history
  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  // Toggle panel
  const togglePanel = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const value = {
    isOpen,
    setIsOpen,
    togglePanel,
    messages,
    isStreaming,
    error,
    settings,
    fetchSettings,
    sendMessage,
    cancelRequest,
    clearMessages,
    applySuggestion,
    dismissSuggestion,
    teddoMemory,
    addPinnedNote,
    removePinnedNote,
    allowDismissedSuggestion,
    clearTeddoMemory,
    sessions,
    currentSessionId,
    createSession,
    switchSession,
    archiveSession,
    unarchiveSession,
    summarizeAndPinSession,
    archivedSessions,
    removeSession,
    availableModels,
    currentModel,
    setModel,
  }

  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  )
}
